import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ChatAlert } from './ChatAlert.tsx';
import Button from '../../core/components/Button.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { useLocalStorage } from '../../core/utils/hooks/useLocalStorage.ts';
import { url } from '../../core/utils/link.ts';
import { CHAT_PATH } from '../constants.ts';
import { useChatContextUsage, useChatUsage } from '../hooks/useChatUsage.ts';

const WARNING_PERCENT = 80;

// warning alert above the composer: title, one line, optional action and a close button; nothing is blocked
const UsageWarning = ({
    title,
    text,
    action = null,
    onClose,
    className,
}: {
    title: string;
    text: string;
    action?: React.ReactNode;
    onClose: () => void;
    className?: string;
}) => (
    <div className={classnames('mx-auto w-full max-w-3xl px-6', className)}>
        <ChatAlert severity="warning" title={title} text={text} actions={action} onClose={onClose} />
    </div>
);

const currentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}`;
};

// the monthly Worki budget is almost used up: shown from 80 % until the 100 % notice takes over, closed once per
// user and month (this browser only)
export const MonthlyBudgetWarning = ({ className }: { className?: string }) => {
    const { t } = useTranslation('chat');
    const { user } = useApplicationContext();
    const { percent, resetDate } = useChatUsage();
    const [dismissed, setDismissed] = useLocalStorage('chat.budgetWarningDismissed', {});

    if (percent < WARNING_PERCENT || percent >= 100 || dismissed[user?.id] === currentMonth()) return null;
    return (
        <UsageWarning
            className={className}
            title={t('usageWarnings.budgetTitle', { percent })}
            text={t('usageWarnings.budgetText', { date: resetDate })}
            onClose={() => setDismissed({ ...dismissed, [user?.id]: currentMonth() })}
        />
    );
};

// the conversation's context window is 80 % used: suggests a new chat, closed once per conversation (this browser only)
export const LongChatWarning = ({ conversationId, className }: { conversationId: string; className?: string }) => {
    const { t } = useTranslation('chat');
    const navigate = useNavigate();
    const percent = useChatContextUsage(conversationId);
    const [dismissed, setDismissed] = useLocalStorage('chat.longChatWarningDismissed', {});

    if (percent === null || percent < WARNING_PERCENT || dismissed[conversationId]) return null;
    return (
        <UsageWarning
            className={className}
            title={t('usageWarnings.chatTitle')}
            text={t('usageWarnings.chatText')}
            action={
                <Button variant="outline" onClick={() => navigate(url(CHAT_PATH))}>
                    {t('usageWarnings.newChat')}
                </Button>
            }
            onClose={() => setDismissed({ ...dismissed, [conversationId]: true })}
        />
    );
};
